import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row, Col, Form, Input, Select, InputNumber, Switch, Button } from 'antd';
import { CustomInputNumber } from 'components/Form/CustomInputNumber';
import { PercelenModel } from 'pages/bodem-en-gewas/percelen-old/models/percelenMock';
import { FC } from 'react';
import { methodes } from '../../settings';

interface PerceelRowProps {
  isKlwReadOnly: boolean;
  gewascodes: PercelenModel['gewascodes'];
  remove: (index: number) => void;
  index: number;
  klwJaar: number;
  gebruikKoppeltool: boolean;
  gebruikKoppeltoolKoolstofBodem: boolean;
}

export const PerceelRow: FC<PerceelRowProps> = ({ gewascodes, remove, index, isKlwReadOnly, klwJaar, gebruikKoppeltool, gebruikKoppeltoolKoolstofBodem }) => {
  const klwJaar2023OrLater = klwJaar >= 2023;
  return (
    <>
      <Row
        style={{
          paddingLeft: 0,
          paddingRight: 0,
        }}
        gutter={12}>
        <Col span={3}>
          <Form.Item name={[index, 'perceelNaam']}>
            <Input disabled={isKlwReadOnly} style={{ height: 32 }} />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item name={[index, 'gewascodeId']}>
            <Select
              disabled={isKlwReadOnly}
              showSearch={{
                filterOption: (input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase()),
              }}
              style={{ textAlign: 'left' }}
              styles={{ popup: { root: { minWidth: 950 } } }}
              options={gewascodes?.map(code => ({ value: code.gewascodeId, label: `${code.code} - ${code.omschrijving}` }))}
            />
          </Form.Item>
        </Col>
        <Col span={2}>
          <Form.Item name={[index, 'grondsoortcategorieId']}>
            <Select
              disabled={isKlwReadOnly}
              showSearch={{
                filterOption: (input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase()),
              }}
              style={{ textAlign: 'left' }}
              options={[
                { value: 1, label: 'Veen' },
                { value: 2, label: 'Klei' },
                { value: 3, label: 'Zand' },
                { value: 4, label: 'Löss' },
              ]}
            />
          </Form.Item>
        </Col>
        {gebruikKoppeltool && (
          <Col span={2}>
            <Form.Item name={[index, 'grondwatertrap']}>
              <Select
                disabled={isKlwReadOnly}
                showSearch={{
                  filterOption: (input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                }}
                style={{ textAlign: 'left' }}
                options={(klwJaar2023OrLater ? ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'] : ['II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII']).map(
                  value => ({
                    value,
                    label: value,
                  })
                )}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={!gebruikKoppeltool && gebruikKoppeltoolKoolstofBodem ? 3 : 2}>
          <Form.Item name={[index, 'oppervlakte']}>
            <CustomInputNumber disabled={isKlwReadOnly} min={0.01} max={500} precision={2} />
          </Form.Item>
        </Col>
        {gebruikKoppeltoolKoolstofBodem && (
          <>
            <Col span={!gebruikKoppeltool && gebruikKoppeltoolKoolstofBodem ? 3 : 2}>
              <Form.Item name={[index, 'soc']}>
                <InputNumber disabled={isKlwReadOnly} decimalSeparator={','} max={100} min={0} precision={2} />
              </Form.Item>
            </Col>
            <Col span={!gebruikKoppeltool && gebruikKoppeltoolKoolstofBodem ? 3 : 1}>
              <Form.Item name={[index, 'organischeStof']}>
                <InputNumber disabled={isKlwReadOnly} decimalSeparator={','} max={100} min={0} precision={2} />
              </Form.Item>
            </Col>
            <Col span={!gebruikKoppeltool && gebruikKoppeltoolKoolstofBodem ? 3 : 1}>
              <Form.Item name={[index, 'lutum']}>
                <InputNumber disabled={isKlwReadOnly} decimalSeparator={','} max={100} min={0} precision={2} />
              </Form.Item>
            </Col>
            <Col span={!gebruikKoppeltool && gebruikKoppeltoolKoolstofBodem ? 3 : 1}>
              <Form.Item name={[index, 'cnRatio']}>
                <InputNumber disabled={isKlwReadOnly} decimalSeparator={','} min={0} precision={2} />
              </Form.Item>
            </Col>
          </>
        )}
        {gebruikKoppeltool && (
          <>
            <Col span={gebruikKoppeltool && !gebruikKoppeltoolKoolstofBodem ? 2 : 1}>
              <Form.Item name={[index, 'pal']}>
                <CustomInputNumber disabled={isKlwReadOnly} />
              </Form.Item>
            </Col>
            <Col span={gebruikKoppeltool && !gebruikKoppeltoolKoolstofBodem ? 2 : 1}>
              <Form.Item name={[index, 'pw']}>
                <InputNumber disabled={isKlwReadOnly} decimalSeparator={','} precision={0} />
              </Form.Item>
            </Col>
            <Col span={gebruikKoppeltool && !gebruikKoppeltoolKoolstofBodem ? 2 : 1}>
              <Form.Item name={[index, 'ppae']}>
                <InputNumber disabled={isKlwReadOnly} decimalSeparator={','} max={10} precision={1} />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Form.Item name={[index, 'fosfaatklasseBepalingMethode']}>
                <Select
                  className="text-right"
                  disabled={isKlwReadOnly}
                  options={Object.keys(methodes)
                    .filter(value => value !== '2')
                    .map(value => ({ value: parseFloat(value), label: methodes[value], className: 'text-right' }))}
                />
              </Form.Item>
            </Col>
            <Col span={1}>
              <Form.Item
                shouldUpdate={(prevValues, nextValues) =>
                  prevValues.gewaspercelen[index]?.fosfaatklasseBepalingMethode !== nextValues.gewaspercelen[index]?.fosfaatklasseBepalingMethode
                }>
                {({ getFieldValue }) => {
                  const methode = getFieldValue(['gewaspercelen', index, 'fosfaatklasseBepalingMethode']);
                  // Only if 2021
                  const hidden = methode === 1 || methode === true;
                  return (
                    <Form.Item name={[index, 'fixerend']} valuePropName="checked" hidden={hidden}>
                      <Switch disabled={isKlwReadOnly} style={{ verticalAlign: 'super' }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </>
        )}
        <Col span={1}>
          <Button disabled={isKlwReadOnly} type="text" style={{ color: 'rgba(0,0,0,.85)' }} onClick={() => remove(index)}>
            <FontAwesomeIcon icon="trash" />
          </Button>
        </Col>
      </Row>
    </>
  );
};
